/**
 * MembersTab.jsx – v6
 * CHANGES:
 *   - Added "Charges" button per member row → opens MemberChargesModal
 *   - Added "Charge Template" download button (Excel with all charge heads as columns)
 *   - Added "Import Charges" Excel upload for bulk charge assignment across all members
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Upload, Download, AlertCircle, Zap } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  getMembers, createMember, updateMember, deleteMember,
  getMemberLedger, getChargeHeads, bulkUploadMemberCharges,
} from '../../api/societybook.api';
import Button             from '../../components/common/Button';
import Input              from '../../components/common/Input';
import Modal              from '../../components/common/Modal';
import MemberChargesModal from './MemberChargesModal';
import toast              from 'react-hot-toast';

const fmt = n => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EMPTY_FORM = {
  flatNo: '', memberName: '', email: '', mobile: '',
  area: '', memberType: 'owner', isOccupied: true,
  openingBalance: '', openingBalanceType: 'Dr',
};

const CHARGE_TYPE_OPTIONS = ['fixed', 'per_sqft', 'per_unit', 'variable'];

// ── Member Excel template (unchanged from v5) ─────────────────────────────────
const downloadMemberTemplate = () => {
  const wb = XLSX.utils.book_new();
  const headers = ['Flat No *', 'Member Name *', 'Member Type', 'Mobile', 'Email', 'Area (sqft)', 'Occupied?', 'Opening Balance', 'Balance Type', 'Notes'];
  const hints   = ['e.g. A-101', 'e.g. Rajesh Sharma', 'owner / tenant', 'e.g. 9876543210', 'e.g. name@email.com', 'e.g. 450', 'yes / no', '0.00', 'Dr / Cr', 'Optional'];
  const samples = [
    ['A-101', 'Rajesh Sharma', 'owner',  '9876543210', 'rajesh@email.com', 450, 'yes', 0,    'Dr', ''],
    ['A-102', 'Priya Mehta',   'owner',  '9876543211', 'priya@email.com',  520, 'yes', 5000, 'Dr', 'Has arrears'],
    ['A-103', 'Suresh Patil',  'tenant', '9876543212', '',                 380, 'yes', 0,    'Dr', ''],
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers, hints, ...samples]);
  ws['!cols'] = [{ wch: 14 }, { wch: 24 }, { wch: 14 }, { wch: 16 }, { wch: 24 }, { wch: 12 }, { wch: 10 }, { wch: 16 }, { wch: 13 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, ws, 'Members');
  XLSX.writeFile(wb, 'Drusshti_Members_Template.xlsx');
};

// ── Charge assignment Excel template (dynamic — includes all charge heads) ────
const downloadChargeTemplate = (members, chargeHeads) => {
  const wb = XLSX.utils.book_new();

  // Build headers: Flat No | Member Name | Area | then per charge head: {Name} Type | {Name} Amount
  const fixedHeaders = ['Flat No', 'Member Name', 'Area (sqft)'];
  const chargeHeaders = [];
  chargeHeads.forEach(h => {
    chargeHeaders.push(`${h.name} (${h.code}) Type`);
    chargeHeaders.push(`${h.name} (${h.code}) Amount`);
  });
  const headers = [...fixedHeaders, ...chargeHeaders];

  const hints = [
    'e.g. A-101', 'Read-only', 'Read-only',
    ...chargeHeads.flatMap(() => ['fixed / per_sqft / per_unit / variable', '0.00 (leave blank if not applicable)']),
  ];

  const rows = members.map(m => {
    const assignMap = {};
    (m.chargeAssignments || []).forEach(a => { assignMap[a.chargeHeadId?.toString()] = a; });

    const chargeValues = chargeHeads.flatMap(h => {
      const a = assignMap[h._id?.toString()];
      return [
        a?.isActive ? (a.chargeType || 'fixed') : '',
        a?.isActive ? (a.amount || '') : '',
      ];
    });
    return [m.flatNo, m.memberName, m.area || '', ...chargeValues];
  });

  const ws = XLSX.utils.aoa_to_sheet([headers, hints, ...rows]);

  // Column widths
  const cols = [{ wch: 14 }, { wch: 24 }, { wch: 12 }];
  chargeHeads.forEach(() => { cols.push({ wch: 28 }); cols.push({ wch: 18 }); });
  ws['!cols'] = cols;

  XLSX.utils.book_append_sheet(wb, ws, 'Charge Assignments');
  XLSX.writeFile(wb, 'Drusshti_Charge_Assignments_Template.xlsx');
};

// ── Parse uploaded charges Excel ──────────────────────────────────────────────
const parseChargesExcel = (file, chargeHeads) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const wb   = XLSX.read(e.target.result, { type: 'array' });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

      // Find header row
      let headerRow = -1;
      for (let i = 0; i < Math.min(rows.length, 5); i++) {
        if (rows[i].some(c => String(c).toLowerCase().includes('flat no'))) {
          headerRow = i; break;
        }
      }
      if (headerRow === -1) { reject(new Error('Could not find header row.')); return; }

      const headers  = rows[headerRow].map(h => String(h).toLowerCase().trim());
      const dataRows = rows.slice(headerRow + 2); // skip header + hints row

      const results = [];
      const errors  = [];

      dataRows.forEach((row, idx) => {
        if (row.every(c => c === '' || c === null || c === undefined)) return;

        const get = (keyword) => {
          const i = headers.findIndex(h => h.includes(keyword.toLowerCase()));
          return i >= 0 ? String(row[i] || '').trim() : '';
        };

        const flatNo = get('flat no') || get('flat');
        if (!flatNo) { errors.push(`Row ${idx + headerRow + 3}: Flat No missing`); return; }

        const assignments = [];
        chargeHeads.forEach(h => {
          const codeKey  = h.code.toLowerCase();
          const nameKey  = h.name.toLowerCase();

          // Match column by code or name in header
          const typeIdx = headers.findIndex(hdr =>
            (hdr.includes(codeKey) || hdr.includes(nameKey)) && hdr.includes('type')
          );
          const amtIdx = headers.findIndex(hdr =>
            (hdr.includes(codeKey) || hdr.includes(nameKey)) && hdr.includes('amount')
          );

          const rawType = typeIdx >= 0 ? String(row[typeIdx] || '').trim().toLowerCase() : '';
          const rawAmt  = amtIdx >= 0  ? row[amtIdx] : '';
          const amount  = parseFloat(rawAmt) || 0;

          if (!rawType && !amount) return; // blank — skip this head for this member

          const chargeType = CHARGE_TYPE_OPTIONS.includes(rawType) ? rawType : 'fixed';

          assignments.push({
            chargeHeadId: h._id,
            chargeType,
            amount,
            isActive: amount > 0,
          });
        });

        results.push({ flatNo, assignments });
      });

      resolve({ results, errors });
    } catch (err) { reject(err); }
  };
  reader.onerror = () => reject(new Error('Failed to read file'));
  reader.readAsArrayBuffer(file);
});

// ── Parse member Excel (same as v5) ──────────────────────────────────────────
const parseMemberExcel = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const wb   = XLSX.read(e.target.result, { type: 'array' });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

      let headerRow = -1;
      for (let i = 0; i < Math.min(rows.length, 5); i++) {
        if (rows[i].some(c => String(c).toLowerCase().includes('flat no'))) {
          headerRow = i; break;
        }
      }
      if (headerRow === -1) { reject(new Error('Could not find header row.')); return; }

      const headers  = rows[headerRow].map(h => String(h).toLowerCase().trim());
      const dataRows = rows.slice(headerRow + 2);
      const members  = [];
      const errors   = [];

      dataRows.forEach((row, idx) => {
        if (row.every(c => c === '' || c === null || c === undefined)) return;
        const get = (keyword) => {
          const i = headers.findIndex(h => h.includes(keyword));
          return i >= 0 ? String(row[i] || '').trim() : '';
        };
        const flatNo     = get('flat no') || get('flat');
        const memberName = get('member name') || get('name');
        if (!flatNo)     { errors.push(`Row ${idx + headerRow + 3}: Flat No missing`);     return; }
        if (!memberName) { errors.push(`Row ${idx + headerRow + 3}: Member Name missing`); return; }
        members.push({
          flatNo, memberName,
          memberType:         ['tenant'].includes(get('member type').toLowerCase()) ? 'tenant' : 'owner',
          isOccupied:         get('occupied') !== 'no',
          mobile:             get('mobile'),
          email:              get('email'),
          area:               parseFloat(get('area')) || 0,
          openingBalance:     parseFloat(get('opening balance')) || 0,
          openingBalanceType: get('balance type').toUpperCase() === 'CR' ? 'Cr' : 'Dr',
        });
      });

      resolve({ members, errors });
    } catch (err) { reject(err); }
  };
  reader.onerror = () => reject(new Error('Failed to read file'));
  reader.readAsArrayBuffer(file);
});

export default function MembersTab({ societyId }) {
  const [members,       setMembers]       = useState([]);
  const [chargeHeads,   setChargeHeads]   = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showModal,     setShowModal]     = useState(false);
  const [editMember,    setEditMember]    = useState(null);
  const [form,          setForm]          = useState(EMPTY_FORM);
  const [saving,        setSaving]        = useState(false);
  const [search,        setSearch]        = useState('');

  // Ledger
  const [ledgerMember,  setLedgerMember]  = useState(null);
  const [ledger,        setLedger]        = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Charges modal
  const [chargesMember, setChargesMember] = useState(null);

  // Member import
  const [showImport,    setShowImport]    = useState(false);
  const [importRows,    setImportRows]    = useState([]);
  const [importErrors,  setImportErrors]  = useState([]);
  const [importing,     setImporting]     = useState(false);
  const [importDone,    setImportDone]    = useState(0);
  const memberFileRef = useRef();

  // Charges import
  const [showChargeImport,   setShowChargeImport]   = useState(false);
  const [chargeImportRows,   setChargeImportRows]   = useState([]);
  const [chargeImportErrors, setChargeImportErrors] = useState([]);
  const [chargeImporting,    setChargeImporting]    = useState(false);
  const chargeFileRef = useRef();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [mRes, chRes] = await Promise.all([
        getMembers(societyId),
        getChargeHeads(societyId),
      ]);
      setMembers(mRes.data.data || []);
      setChargeHeads((chRes.data.data || []).filter(h => h.code !== 'GST'));
    } catch { toast.error('Failed to load members'); }
    finally { setLoading(false); }
  }, [societyId]);

  useEffect(() => { load(); }, [load]);

  const sf = key => e => setForm(f => ({ ...f, [key]: e.target.value }));

  const openAdd  = () => { setEditMember(null); setForm({ ...EMPTY_FORM }); setShowModal(true); };
  const openEdit = (m) => {
    setEditMember(m);
    setForm({
      flatNo: m.flatNo, memberName: m.memberName,
      email: m.email || '', mobile: m.mobile || '',
      area: m.area || '', memberType: m.memberType,
      isOccupied: m.isOccupied,
      openingBalance: m.openingBalance || '',
      openingBalanceType: m.openingBalanceType || 'Dr',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.flatNo.trim() || !form.memberName.trim()) { toast.error('Flat No and Name are required'); return; }
    try {
      setSaving(true);
      const payload = { ...form, societyId, area: parseFloat(form.area) || 0, openingBalance: parseFloat(form.openingBalance) || 0 };
      if (editMember) await updateMember(editMember._id, payload);
      else            await createMember(payload);
      toast.success(editMember ? 'Member updated' : 'Member added');
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || err.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Remove ${m.memberName} (${m.flatNo})?`)) return;
    try { await deleteMember(m._id); toast.success('Member removed'); load(); }
    catch { toast.error('Failed'); }
  };

  const openLedger = async (m) => {
    setLedgerMember(m);
    setLedger([]);
    setLedgerLoading(true);
    try {
      const res = await getMemberLedger(m._id);
      setLedger(res.data.data || []);
    } catch { toast.error('Failed to load ledger'); }
    finally { setLedgerLoading(false); }
  };

  // ── Member import ───────────────────────────────────────────────────────────
  const handleMemberFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    try {
      const { members, errors } = await parseMemberExcel(file);
      setImportRows(members);
      setImportErrors(errors);
      setImportDone(0);
      setShowImport(true);
    } catch (err) { toast.error(err.message || 'Failed to parse file'); }
  };

  const handleImport = async () => {
    setImporting(true);
    setImportDone(0);
    let done = 0;
    for (const m of importRows) {
      try {
        await createMember({ ...m, societyId });
        done++;
        setImportDone(done);
      } catch {}
    }
    toast.success(`${done} member(s) imported`);
    setShowImport(false);
    load();
    setImporting(false);
  };

  // ── Charges import ──────────────────────────────────────────────────────────
  const handleChargeFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (chargeHeads.length === 0) { toast.error('No charge heads configured yet'); return; }
    try {
      const { results, errors } = await parseChargesExcel(file, chargeHeads);
      setChargeImportRows(results);
      setChargeImportErrors(errors);
      setShowChargeImport(true);
    } catch (err) { toast.error(err.message || 'Failed to parse file'); }
  };

  const handleChargeImport = async () => {
    try {
      setChargeImporting(true);
      // Build rows payload: flatten all assignments keyed by flatNo
      const rows = chargeImportRows.flatMap(r =>
        r.assignments.map(a => ({ flatNo: r.flatNo, ...a }))
      );
      const res = await bulkUploadMemberCharges(societyId, rows);
      toast.success(`Charges updated for ${res.data.updated} member(s)${res.data.notFound?.length > 0 ? `. Not found: ${res.data.notFound.join(', ')}` : ''}`);
      setShowChargeImport(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || err.message || 'Failed'); }
    finally { setChargeImporting(false); }
  };

  const filtered = members.filter(m =>
    m.flatNo?.toLowerCase().includes(search.toLowerCase()) ||
    m.memberName?.toLowerCase().includes(search.toLowerCase())
  );

  const totalMembers = members.length;
  const occupied     = members.filter(m => m.isOccupied).length;
  const tenants      = members.filter(m => m.memberType === 'tenant').length;

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      {/* Summary cards */}
      <div className="fy-stats-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '1.5rem' }}>
        <div className="fy-stat-card">
          <div className="fy-stat-top"><span className="fy-stat-label">Total Members</span></div>
          <div className="fy-stat-value">{totalMembers}</div>
        </div>
        <div className="fy-stat-card">
          <div className="fy-stat-top"><span className="fy-stat-label">Occupied</span></div>
          <div className="fy-stat-value">{occupied}</div>
        </div>
        <div className="fy-stat-card">
          <div className="fy-stat-top"><span className="fy-stat-label">Tenants</span></div>
          <div className="fy-stat-value">{tenants}</div>
        </div>
      </div>

      {/* Members table */}
      <div className="section-card">
        <div className="section-card-head">
          <h3>Members</h3>
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input className="field-input" placeholder="Search flat / name…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: 200, padding: '0.38rem 0.75rem', fontSize: '0.82rem' }} />

            {/* Member import */}
            <Button size="sm" variant="ghost" icon={Download} onClick={downloadMemberTemplate}>Template</Button>
            <input type="file" accept=".xlsx,.xls" ref={memberFileRef} style={{ display: 'none' }} onChange={handleMemberFileChange} />
            <Button size="sm" variant="ghost" icon={Upload} onClick={() => memberFileRef.current?.click()}>Import Members</Button>

            {/* Charge import */}
            <Button size="sm" variant="ghost" icon={Download}
              onClick={() => {
                if (chargeHeads.length === 0) { toast.error('No charge heads configured yet'); return; }
                downloadChargeTemplate(members, chargeHeads);
              }}>
              Charge Template
            </Button>
            <input type="file" accept=".xlsx,.xls" ref={chargeFileRef} style={{ display: 'none' }} onChange={handleChargeFileChange} />
            <Button size="sm" variant="ghost" icon={Zap} onClick={() => chargeFileRef.current?.click()}>Import Charges</Button>

            <Button size="sm" icon={Plus} onClick={openAdd}>Add Member</Button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state" style={{ padding: '3rem' }}>
            <AlertCircle size={36} strokeWidth={1} />
            <h3>No members found</h3>
            <p>Add members manually or import from Excel</p>
            <Button icon={Plus} onClick={openAdd} className="mt-2">Add Member</Button>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Flat</th>
                <th>Member Name</th>
                <th>Type</th>
                <th style={{ textAlign: 'right' }}>Area (sqft)</th>
                <th>Mobile</th>
                <th style={{ textAlign: 'right' }}>Opening Bal</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => (
                <tr key={m._id}>
                  <td><span className="flat-tag">{m.flatNo}</span></td>
                  <td style={{ fontWeight: 500, fontSize: '0.9rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span className="member-avatar" style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--tc-light)', color: 'var(--tc)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.78rem', flexShrink: 0 }}>
                        {m.memberName.charAt(0).toUpperCase()}
                      </span>
                      {m.memberName}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${m.memberType === 'owner' ? 'badge-info' : 'badge-warning'}`}>
                      {m.memberType.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    {m.area ? `${m.area} sqft` : '—'}
                  </td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{m.mobile || '—'}</td>
                  <td style={{ textAlign: 'right', fontSize: '0.82rem' }}>
                    {m.openingBalance > 0
                      ? <span className={m.openingBalanceType === 'Dr' ? 'negative-amount' : 'positive-amount'}>
                          ₹{fmt(m.openingBalance)} {m.openingBalanceType}
                        </span>
                      : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.35rem' }}>
                      <Button size="xs" variant="ghost" onClick={() => openLedger(m)}>Ledger</Button>
                      <Button size="xs" variant="ghost" onClick={() => setChargesMember(m)}>Charges</Button>
                      <Button size="xs" variant="ghost" onClick={() => openEdit(m)}>Edit</Button>
                      <Button size="xs" variant="danger" icon={Trash2} onClick={() => handleDelete(m)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Member Charges Modal ── */}
      <MemberChargesModal
        member={chargesMember}
        onClose={() => setChargesMember(null)}
        onSaved={load}
      />

      {/* ── Charge Import Preview Modal ── */}
      <Modal isOpen={showChargeImport} onClose={() => setShowChargeImport(false)}
        title="Import Charge Assignments"
        subtitle={`${chargeImportRows.length} member(s) found in file`}
        size="lg"
        footer={
          <>
            <Button variant="subtle" onClick={() => setShowChargeImport(false)}>Cancel</Button>
            <Button loading={chargeImporting} icon={Upload} onClick={handleChargeImport} disabled={chargeImportRows.length === 0}>
              Import for {chargeImportRows.length} Member(s)
            </Button>
          </>
        }>
        <div>
          {chargeImportErrors.length > 0 && (
            <div style={{ padding: '0.85rem 1rem', background: 'var(--danger-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(240,107,107,0.2)', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--danger)', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <AlertCircle size={13} /> {chargeImportErrors.length} issue(s) found
              </div>
              {chargeImportErrors.map((e, i) => <div key={i} style={{ fontSize: '0.78rem', color: 'var(--danger)', marginBottom: '0.2rem' }}>{e}</div>)}
            </div>
          )}

          {chargeImportRows.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Flat No</th>
                    <th>Charges Being Set</th>
                  </tr>
                </thead>
                <tbody>
                  {chargeImportRows.map((r, i) => (
                    <tr key={i}>
                      <td><span className="flat-tag">{r.flatNo}</span></td>
                      <td style={{ fontSize: '0.8rem' }}>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {r.assignments.map((a, j) => {
                            const head = chargeHeads.find(h => h._id === a.chargeHeadId || h._id?.toString() === a.chargeHeadId?.toString());
                            return (
                              <span key={j} style={{ padding: '0.2rem 0.5rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-xs)', border: '1px solid var(--border-subtle)', fontSize: '0.75rem', color: 'var(--text)' }}>
                                {head?.code || '?'}: ₹{a.amount} ({a.chargeType})
                              </span>
                            );
                          })}
                          {r.assignments.length === 0 && <span style={{ color: 'var(--text-dim)', fontStyle: 'italic' }}>No charges</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>

      {/* ── Member Import Preview Modal ── */}
      <Modal isOpen={showImport} onClose={() => setShowImport(false)}
        title="Import Members from Excel"
        subtitle={`${importRows.length} member(s) found in file`}
        size="lg"
        footer={
          <>
            <Button variant="subtle" onClick={() => setShowImport(false)}>Cancel</Button>
            <Button loading={importing} icon={Upload} onClick={handleImport} disabled={importRows.length === 0}>
              Import {importRows.length} Member(s)
            </Button>
          </>
        }>
        <div>
          {importErrors.length > 0 && (
            <div style={{ padding: '0.85rem 1rem', background: 'var(--danger-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(240,107,107,0.2)', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--danger)', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <AlertCircle size={13} /> {importErrors.length} issue(s) found
              </div>
              {importErrors.map((e, i) => <div key={i} style={{ fontSize: '0.78rem', color: 'var(--danger)', marginBottom: '0.2rem' }}>{e}</div>)}
            </div>
          )}
          {importing && (
            <div style={{ padding: '0.75rem 1rem', background: 'var(--info-bg)', borderRadius: 'var(--radius-sm)', marginBottom: '1rem', fontSize: '0.82rem', color: 'var(--info)' }}>
              Importing… {importDone} / {importRows.length} done
            </div>
          )}
          {importRows.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr><th>Flat No</th><th>Member Name</th><th>Type</th><th>Area</th><th>Mobile</th><th>Opening Bal</th></tr>
                </thead>
                <tbody>
                  {importRows.map((r, i) => (
                    <tr key={i}>
                      <td><span className="flat-tag">{r.flatNo}</span></td>
                      <td style={{ fontSize: '0.85rem' }}>{r.memberName}</td>
                      <td><span className={`badge ${r.memberType === 'owner' ? 'badge-info' : 'badge-warning'}`}>{r.memberType}</span></td>
                      <td style={{ fontSize: '0.82rem' }}>{r.area || '—'}</td>
                      <td style={{ fontSize: '0.82rem' }}>{r.mobile || '—'}</td>
                      <td style={{ fontSize: '0.82rem' }}>{r.openingBalance > 0 ? `₹${fmt(r.openingBalance)} ${r.openingBalanceType}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>

      {/* ── Add/Edit Member Modal ── */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}
        title={editMember ? 'Edit Member' : 'Add Member'}
        subtitle={editMember ? `Editing ${editMember.flatNo}` : 'Add to society'}
        size="md"
        footer={
          <>
            <Button variant="subtle" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button loading={saving} icon={Plus} onClick={handleSave}>
              {editMember ? 'Update' : 'Add Member'}
            </Button>
          </>
        }>
        <div className="txn-form">
          <div className="txn-form-grid">
            <Input label="Flat No *" value={form.flatNo} onChange={sf('flatNo')} placeholder="e.g. A-101" />
            <div className="field">
              <label className="field-label">Member Type</label>
              <div className="field-control">
                <select className="field-input" value={form.memberType} onChange={sf('memberType')}>
                  <option value="owner">Owner</option>
                  <option value="tenant">Tenant</option>
                </select>
              </div>
            </div>
          </div>
          <Input label="Member Name *" value={form.memberName} onChange={sf('memberName')} placeholder="Full name" />
          <div className="txn-form-grid">
            <Input label="Mobile" value={form.mobile} onChange={sf('mobile')} placeholder="+91 98765 43210" />
            <Input label="Email" type="email" value={form.email} onChange={sf('email')} placeholder="email@example.com" />
          </div>
          <div className="txn-form-grid">
            <Input label="Area (sqft)" type="number" value={form.area} onChange={sf('area')} placeholder="0" />
            <div className="field">
              <label className="field-label">Occupied?</label>
              <div className="field-control">
                <select className="field-input" value={form.isOccupied ? 'yes' : 'no'}
                  onChange={e => setForm(f => ({ ...f, isOccupied: e.target.value === 'yes' }))}>
                  <option value="yes">Yes</option>
                  <option value="no">No (Non-Occupancy)</option>
                </select>
              </div>
            </div>
          </div>
          <div className="txn-form-grid">
            <Input label="Opening Balance (₹)" type="number" value={form.openingBalance} onChange={sf('openingBalance')} placeholder="0.00" />
            <div className="field">
              <label className="field-label">Balance Type</label>
              <div className="field-control">
                <select className="field-input" value={form.openingBalanceType} onChange={sf('openingBalanceType')}>
                  <option value="Dr">Dr — Member owes society</option>
                  <option value="Cr">Cr — Advance paid</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* ── Member Ledger Modal ── */}
      <Modal isOpen={!!ledgerMember} onClose={() => setLedgerMember(null)}
        title="Member Ledger"
        subtitle={ledgerMember ? `${ledgerMember.flatNo} — ${ledgerMember.memberName}` : ''}
        size="lg"
        footer={<Button variant="subtle" onClick={() => setLedgerMember(null)}>Close</Button>}>
        {ledgerLoading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : ledger.length === 0 ? (
          <div className="empty-state" style={{ padding: '2rem' }}><p>No transactions yet.</p></div>
        ) : (
          <table className="ledger-table">
            <thead>
              <tr>
                <th>Date</th><th>Type</th><th>Particulars</th>
                <th className="amount-cell">Debit</th>
                <th className="amount-cell">Credit</th>
                <th className="amount-cell">Balance</th>
              </tr>
            </thead>
            <tbody>
              {ledger.map((e, i) => (
                <tr key={i}>
                  <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{new Date(e.entryDate).toLocaleDateString('en-IN')}</td>
                  <td><span className={`badge ${e.entryType === 'payment' ? 'badge-success' : e.entryType === 'bill' ? 'badge-warning' : 'badge-subtle'}`}>{e.entryType}</span></td>
                  <td style={{ fontSize: '0.82rem' }}>{e.particulars}</td>
                  <td className="amount-cell">{e.debitAmount > 0 ? <span className="negative-amount">₹{fmt(e.debitAmount)}</span> : '—'}</td>
                  <td className="amount-cell">{e.creditAmount > 0 ? <span className="positive-amount">₹{fmt(e.creditAmount)}</span> : '—'}</td>
                  <td className="amount-cell">
                    <span className={e.balanceType === 'Dr' ? 'negative-amount' : 'positive-amount'}>
                      ₹{fmt(Math.abs(e.runningBalance))} {e.balanceType}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal>
    </div>
  );
}
